import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, StudentSummary, TestSummary, ClassSummary, ClassDetail, AvailableConcept } from '../api';
import './TeacherDashboard.css';

type Tab = 'students' | 'tests' | 'classes';

interface StudentForm {
  name: string;
  email: string;
  password: string;
  gradeLevel: number;
}

export default function TeacherDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('students');
  const [students, setStudents] = useState<StudentSummary[]>([]);
  const [tests, setTests] = useState<TestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState<number | null>(null);

  // Student form state
  const [showModal, setShowModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<StudentSummary | null>(null);
  const [formData, setFormData] = useState<StudentForm>({ name: '', email: '', password: '', gradeLevel: 8 });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // Class management state
  const [classes, setClasses] = useState<ClassSummary[]>([]);
  const [selectedClass, setSelectedClass] = useState<ClassDetail | null>(null);
  const [availableConcepts, setAvailableConcepts] = useState<AvailableConcept[]>([]);
  const [showClassModal, setShowClassModal] = useState(false);
  const [editingClass, setEditingClass] = useState<ClassSummary | null>(null);
  const [classFormName, setClassFormName] = useState('');
  const [showAssignModal, setShowAssignModal] = useState<'students' | 'concepts' | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [studentsData, testsData, classesData] = await Promise.all([
        api.getStudents(),
        api.getTests(),
        api.getClasses()
      ]);
      setStudents(studentsData);
      setTests(testsData);
      setClasses(classesData);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadClassDetail = async (classId: number) => {
    try {
      const [classData, concepts] = await Promise.all([
        api.getClass(classId),
        api.getAvailableConcepts(classId)
      ]);
      setSelectedClass(classData);
      setAvailableConcepts(concepts);
    } catch (err) {
      console.error('Failed to load class:', err);
    }
  };

  const openClassModal = (cls?: ClassSummary) => {
    setEditingClass(cls || null);
    setClassFormName(cls?.name || '');
    setShowClassModal(true);
  };

  const closeClassModal = () => {
    setShowClassModal(false);
    setEditingClass(null);
    setClassFormName('');
  };

  const handleClassSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingClass) {
        await api.updateClass(editingClass.id, classFormName);
      } else {
        await api.createClass(classFormName);
      }
      await loadData();
      closeClassModal();
    } catch (err) {
      console.error('Failed to save class:', err);
    } finally {
      setSaving(false);
    }
  };

  const deleteClass = async (classId: number) => {
    if (!confirm('Delete this class? Students and concepts will be unassigned but not deleted.')) return;
    try {
      await api.deleteClass(classId);
      setSelectedClass(null);
      await loadData();
    } catch (err) {
      console.error('Failed to delete class:', err);
    }
  };

  const openAssignModal = (type: 'students' | 'concepts') => {
    setShowAssignModal(type);
    setSelectedIds([]);
  };

  const handleAssign = async () => {
    if (!selectedClass || selectedIds.length === 0) return;
    setSaving(true);
    try {
      if (showAssignModal === 'students') {
        await api.assignStudentsToClass(selectedClass.id, selectedIds);
      } else {
        await api.assignConceptsToClass(selectedClass.id, selectedIds);
      }
      await loadClassDetail(selectedClass.id);
      await loadData();
      setShowAssignModal(null);
      setSelectedIds([]);
    } catch (err) {
      console.error('Failed to assign:', err);
    } finally {
      setSaving(false);
    }
  };

  const removeStudentFromClass = async (studentId: number) => {
    if (!selectedClass) return;
    try {
      await api.removeStudentFromClass(selectedClass.id, studentId);
      await loadClassDetail(selectedClass.id);
      await loadData();
    } catch (err) {
      console.error('Failed to remove student:', err);
    }
  };

  const removeConceptFromClass = async (conceptId: number) => {
    if (!selectedClass) return;
    try {
      await api.removeConceptFromClass(selectedClass.id, conceptId);
      await loadClassDetail(selectedClass.id);
      await loadData();
    } catch (err) {
      console.error('Failed to remove concept:', err);
    }
  };

  const generateTest = async (studentId: number) => {
    setGenerating(studentId);
    try {
      await api.generateTest(studentId, 10);
      await loadData();
      alert('Test generated successfully!');
    } catch (err) {
      console.error('Failed to generate test:', err);
      alert('Failed to generate test. Make sure the student has practiced some concepts first.');
    } finally {
      setGenerating(null);
    }
  };

  const openAddModal = () => {
    setEditingStudent(null);
    setFormData({ name: '', email: '', password: '', gradeLevel: 8 });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (student: StudentSummary) => {
    setEditingStudent(student);
    setFormData({ name: student.name, email: student.email, password: '', gradeLevel: student.gradeLevel });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingStudent(null);
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);

    try {
      if (editingStudent) {
        await api.updateStudent(editingStudent.id, {
          name: formData.name,
          email: formData.email,
          gradeLevel: formData.gradeLevel
        });
      } else {
        if (!formData.password) {
          setFormError('Password is required for new students');
          setSaving(false);
          return;
        }
        await api.addStudent(formData.name, formData.email, formData.password, formData.gradeLevel);
      }
      await loadData();
      closeModal();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save student');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="teacher-dashboard">
      <header className="header">
        <h1>KillerHomework</h1>
        <div className="header-actions">
          <Link to="/lessons" className="btn btn-primary">Manage Lessons</Link>
          <span className="user-name">{user?.name} (Teacher)</span>
          <button onClick={logout} className="btn btn-secondary">Logout</button>
        </div>
      </header>

      <main className="teacher-content">
        <div className="tab-nav">
          <button
            className={`tab-btn ${activeTab === 'students' ? 'active' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            Students
          </button>
          <button
            className={`tab-btn ${activeTab === 'classes' ? 'active' : ''}`}
            onClick={() => setActiveTab('classes')}
          >
            Classes
          </button>
          <button
            className={`tab-btn ${activeTab === 'tests' ? 'active' : ''}`}
            onClick={() => setActiveTab('tests')}
          >
            Monday Tests
          </button>
        </div>

        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : activeTab === 'students' ? (
          <div className="students-tab">
            <div className="tab-header">
              <h2>Student Progress</h2>
              <button className="btn btn-primary" onClick={openAddModal}>+ Add Student</button>
            </div>
            <div className="students-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Concepts Mastered</th>
                    <th>Total Problems</th>
                    <th>Accuracy</th>
                    <th>Last Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(student => (
                    <tr key={student.id}>
                      <td>
                        <div className="student-name">
                          {student.name}
                          <span className="xp-badge">{student.xpPoints} XP</span>
                        </div>
                      </td>
                      <td>{student.masteredConcepts}</td>
                      <td>{student.totalAttempts}</td>
                      <td>
                        <span className={`accuracy ${student.accuracy >= 80 ? 'high' : student.accuracy >= 60 ? 'medium' : 'low'}`}>
                          {student.accuracy}%
                        </span>
                      </td>
                      <td>
                        {student.lastLogin
                          ? new Date(student.lastLogin).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="action-buttons">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEditModal(student)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => generateTest(student.id)}
                          disabled={generating === student.id}
                        >
                          {generating === student.id ? 'Generating...' : 'Generate Test'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === 'classes' ? (
          <div className="classes-tab">
            {selectedClass ? (
              <div className="class-detail">
                <div className="class-detail-header">
                  <button className="btn btn-secondary btn-sm" onClick={() => setSelectedClass(null)}>
                    &larr; Back to Classes
                  </button>
                  <h2>{selectedClass.name}</h2>
                  <div className="action-buttons">
                    <button className="btn btn-secondary btn-sm" onClick={() => openClassModal(classes.find(c => c.id === selectedClass.id))}>
                      Rename
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteClass(selectedClass.id)}>
                      Delete
                    </button>
                  </div>
                </div>

                <div className="class-sections">
                  <div className="class-section">
                    <div className="section-header">
                      <h3>Students ({selectedClass.students.length})</h3>
                      <button className="btn btn-primary btn-sm" onClick={() => openAssignModal('students')}>
                        + Add Students
                      </button>
                    </div>
                    {selectedClass.students.length === 0 ? (
                      <p className="empty-state">No students assigned to this class yet.</p>
                    ) : (
                      <ul className="item-list">
                        {selectedClass.students.map(s => (
                          <li key={s.id}>
                            <span>{s.name} ({s.email})</span>
                            <button className="btn-remove" onClick={() => removeStudentFromClass(s.id)}>Remove</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="class-section">
                    <div className="section-header">
                      <h3>Concepts ({selectedClass.concepts.length})</h3>
                      <button className="btn btn-primary btn-sm" onClick={() => openAssignModal('concepts')}>
                        + Add Concepts
                      </button>
                    </div>
                    {selectedClass.concepts.length === 0 ? (
                      <p className="empty-state">No concepts assigned to this class yet.</p>
                    ) : (
                      <ul className="item-list">
                        {selectedClass.concepts.map(c => (
                          <li key={c.id}>
                            <span>Lesson {c.lesson_number}: {c.title}</span>
                            <button className="btn-remove" onClick={() => removeConceptFromClass(c.id)}>Remove</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="tab-header">
                  <h2>Classes</h2>
                  <button className="btn btn-primary" onClick={() => openClassModal()}>+ New Class</button>
                </div>
                {classes.length === 0 ? (
                  <p className="no-tests">No classes yet. Create a class to organize students and homework.</p>
                ) : (
                  <div className="classes-grid">
                    {classes.map(cls => (
                      <div key={cls.id} className="class-card" onClick={() => loadClassDetail(cls.id)}>
                        <h3>{cls.name}</h3>
                        <div className="class-stats">
                          <span>{cls.student_count} students</span>
                          <span>{cls.concept_count} concepts</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="tests-tab">
            <h2>Monday Tests</h2>
            <div className="tests-list">
              {tests.length === 0 ? (
                <p className="no-tests">No tests generated yet. Generate a test for a student from the Students tab.</p>
              ) : (
                <div className="tests-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Date</th>
                        <th>Questions</th>
                        <th>Status</th>
                        <th>Score</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tests.map(test => (
                        <tr key={test.id}>
                          <td>{test.studentName}</td>
                          <td>{new Date(test.date).toLocaleDateString()}</td>
                          <td>{test.questionCount}</td>
                          <td>
                            <span className={`status-badge ${test.graded ? 'graded' : 'pending'}`}>
                              {test.graded ? 'Graded' : 'Pending'}
                            </span>
                          </td>
                          <td>
                            {test.score !== null ? `${Math.round(test.score)}%` : '-'}
                          </td>
                          <td>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => window.open(`/api/teacher/tests/${test.id}`, '_blank')}
                            >
                              {test.graded ? 'View' : 'Grade'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{editingStudent ? 'Edit Student' : 'Add Student'}</h3>
            <form onSubmit={handleSubmit}>
              {formError && <div className="error-message">{formError}</div>}
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="input"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              {!editingStudent && (
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    className="input"
                    value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                </div>
              )}
              <div className="form-group">
                <label>Grade Level</label>
                <select
                  className="input"
                  value={formData.gradeLevel}
                  onChange={e => setFormData({ ...formData, gradeLevel: parseInt(e.target.value) })}
                >
                  {[6, 7, 8, 9].map(g => <option key={g} value={g}>Grade {g}</option>)}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showClassModal && (
        <div className="modal-overlay" onClick={closeClassModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{editingClass ? 'Rename Class' : 'Create New Class'}</h3>
            <form onSubmit={handleClassSubmit}>
              <div className="form-group">
                <label>Class Name</label>
                <input
                  type="text"
                  className="input"
                  value={classFormName}
                  onChange={e => setClassFormName(e.target.value)}
                  placeholder="e.g., This Week's Homework"
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeClassModal}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssignModal && selectedClass && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(null)}>
          <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
            <h3>Add {showAssignModal === 'students' ? 'Students' : 'Concepts'} to {selectedClass.name}</h3>
            <div className="assign-list">
              {showAssignModal === 'students' ? (
                students
                  .filter(s => !selectedClass.students.find(cs => cs.id === s.id))
                  .map(s => (
                    <label key={s.id} className="assign-item">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(s.id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedIds([...selectedIds, s.id]);
                          } else {
                            setSelectedIds(selectedIds.filter(id => id !== s.id));
                          }
                        }}
                      />
                      <span>{s.name} ({s.email})</span>
                    </label>
                  ))
              ) : (
                availableConcepts.map(c => (
                  <label key={c.id} className="assign-item">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedIds([...selectedIds, c.id]);
                        } else {
                          setSelectedIds(selectedIds.filter(id => id !== c.id));
                        }
                      }}
                    />
                    <span>Lesson {c.lesson_number}: {c.title}</span>
                  </label>
                ))
              )}
              {((showAssignModal === 'students' && students.filter(s => !selectedClass.students.find(cs => cs.id === s.id)).length === 0) ||
                (showAssignModal === 'concepts' && availableConcepts.length === 0)) && (
                <p className="empty-state">
                  {showAssignModal === 'students' ? 'All students are already in this class.' : 'All concepts are already in this class.'}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(null)}>Cancel</button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAssign}
                disabled={saving || selectedIds.length === 0}
              >
                {saving ? 'Adding...' : `Add ${selectedIds.length} Selected`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
